import type { Instruction } from './commands';

export interface Clearance { callsign: string; instructions: Instruction[] }
export type ClearanceValidation = { ok: true; clearance: Clearance } | { ok: false; message: string };

/** Validate untrusted tool arguments before they reach the command executor. */
export function validateClearance(raw: unknown): ClearanceValidation {
  const fail = (message: string): ClearanceValidation => ({ ok: false, message });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fail('Repeat the callsign and clearance.');
  const r = raw as Record<string, unknown>;
  if (Object.keys(r).some((k) => !['callsign', 'instructions'].includes(k))) return fail('Unsupported clearance fields.');
  if (typeof r.callsign !== 'string' || !/^[A-Z0-9]{2,8}$/.test(r.callsign)) return fail('Use an exact aircraft callsign.');
  if (!Array.isArray(r.instructions) || r.instructions.length < 1 || r.instructions.length > 8) return fail('Use one to eight supported instructions.');
  const instructions: Instruction[] = [];
  for (const item of r.instructions) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return fail('Unsupported clearance.');
    const i = item as Record<string, unknown>;
    const fields: Record<string, readonly string[]> = { heading: ['headingDeg', 'turn'], altitude: ['altitudeFt', 'expedite'], speed: ['speedKt'], land: ['runway'], approach: ['runway'], direct: ['fix', 'turn'], hold: ['fix'], takeoff: [], lineUp: [], abort: [], resumeNav: [], contact: [] };
    const allowed = typeof i.kind === 'string' ? fields[i.kind] : undefined;
    if (!allowed || Object.keys(i).some((k) => k !== 'kind' && !allowed.includes(k))) return fail('Unsupported instruction fields.');
    const number = (key: string, min: number, max: number) => typeof i[key] === 'number' && Number.isFinite(i[key]) && Number.isInteger(i[key]) && (i[key] as number) >= min && (i[key] as number) <= max;
    const turn = i.turn === undefined || i.turn === 'L' || i.turn === 'R';
    switch (i.kind) {
      case 'heading':
        if (!number('headingDeg', 1, 360) || !turn) return fail('Heading must be 001 to 360.');
        instructions.push({ kind: 'heading', headingDeg: i.headingDeg as number, ...(i.turn ? { turn: i.turn as 'L' | 'R' } : {}) }); break;
      case 'altitude':
        if (!number('altitudeFt', 500, 60000) || (i.expedite !== undefined && typeof i.expedite !== 'boolean')) return fail('Use an altitude in feet.');
        instructions.push({ kind: 'altitude', altitudeFt: i.altitudeFt as number, expedite: i.expedite === true }); break;
      case 'speed':
        if (!number('speedKt', 50, 600)) return fail('Use a speed in knots.');
        instructions.push({ kind: 'speed', speedKt: i.speedKt as number }); break;
      case 'land': case 'approach':
        if (typeof i.runway !== 'string' || !/^\d{1,2}[LRC]?$/.test(i.runway)) return fail('Repeat the runway and side.');
        instructions.push({ kind: i.kind, runway: i.runway }); break;
      case 'direct': case 'hold':
        if (typeof i.fix !== 'string' || !/^[A-Z][A-Z0-9_]{1,7}$/.test(i.fix) || !turn) return fail('Repeat the navigation fix.');
        instructions.push(i.kind === 'hold' ? { kind: 'hold', fix: i.fix } : { kind: 'direct', fix: i.fix, ...(i.turn ? { turn: i.turn as 'L' | 'R' } : {}) }); break;
      case 'takeoff': case 'lineUp': case 'abort': case 'resumeNav': case 'contact': instructions.push({ kind: i.kind }); break;
      default: return fail('That instruction is not supported.');
    }
  }
  return { ok: true, clearance: { callsign: r.callsign, instructions } };
}
